import { useCallback, useState } from "react";

// Views every TaskManagement page now supports.
const VALID_VIEWS = ["list", "table", "kanban"];

/**
 * Persists the chosen view mode (list/table/kanban) per page in localStorage so
 * a user's preference for a particular page sticks across visits and reloads.
 *
 * @param {string} pageId - Stable identifier for the page (same id used elsewhere).
 * @param {string} [defaultMode="list"] - View to fall back to when nothing is stored.
 * @returns {[string, function]} Current view mode and a setter that persists it.
 */
export default function usePageViewMode(pageId, defaultMode = "list") {
  const storageKey = `${pageId}-view-mode`;

  const [viewMode, setViewModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored && VALID_VIEWS.includes(stored) ? stored : defaultMode;
    } catch {
      return defaultMode;
    }
  });

  const setViewMode = useCallback(
    (mode) => {
      if (!VALID_VIEWS.includes(mode)) return;
      setViewModeState(mode);
      try {
        localStorage.setItem(storageKey, mode);
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }
    },
    [storageKey],
  );

  return [viewMode, setViewMode];
}
